import { useRef, useState } from "react";
import { toast } from "sonner";
import { FolderUp, Loader2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { matchFile } from "@/lib/matchFile";

export interface ArticleOpt { slug: string; title: string; projects: { index: number; title: string }[] }
export interface Item { id: string; file: File; preview: string; slug: string; spot: string; state: "" | "busy" | "done" | "error"; error?: string }

const LIB = "__library";

async function readEntry(entry: any): Promise<File[]> {
  if (entry.isFile) return new Promise((res) => entry.file((f: File) => res([f]), () => res([])));
  if (!entry.isDirectory) return [];
  const reader = entry.createReader();
  const all: File[] = [];
  for (;;) {
    const batch: any[] = await new Promise((res) => reader.readEntries(res, () => res([])));
    if (!batch.length) return all;
    for (const e of batch) all.push(...(await readEntry(e)));
  }
}

export function BulkUpload({ rows, onDone }: { rows: ArticleOpt[]; onDone: () => void }) {
  const [items, setItems] = useState<Item[]>([]);
  const [over, setOver] = useState(false);
  const [running, setRunning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const add = (files: File[]) => {
    const imgs = files.filter((f) => /^image\//.test(f.type) || /\.(jpe?g|png|webp)$/i.test(f.name));
    if (imgs.length < files.length) { const n = files.length - imgs.length; toast.message(`${n} non-image file${n === 1 ? "" : "s"} skipped`); }
    setItems((cur) => [...cur, ...imgs.map((f) => {
      const m = matchFile(f.name, rows);
      return { id: `${f.name}-${f.size}-${Math.random()}`, file: f, preview: URL.createObjectURL(f), slug: m ? m.slug : LIB, spot: "", state: "" as const };
    })]);
  };
  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault(); setOver(false);
    const entries = Array.from(e.dataTransfer.items || []).map((i: any) => i.webkitGetAsEntry?.()).filter(Boolean);
    add(entries.length ? (await Promise.all(entries.map(readEntry))).flat() : Array.from(e.dataTransfer.files));
  };
  const patch = (id: string, p: Partial<Item>) => setItems((cur) => cur.map((i) => (i.id === id ? { ...i, ...p } : i)));
  const ready = (i: Item) => i.slug === LIB || i.spot !== "";
  const todo = items.filter((i) => i.state !== "done");
  const blocked = todo.filter((i) => !ready(i)).length;

  const uploadAll = async () => {
    setRunning(true);
    let ok = 0;
    for (const i of todo) {
      patch(i.id, { state: "busy", error: undefined });
      const fd = new FormData(); fd.append("file", i.file);
      const url = i.slug === LIB ? "/admin/images/library" : i.spot === "hero" ? `/admin/posts/${i.slug}/image` : `/admin/posts/${i.slug}/project-image/${i.spot}`;
      try { await api.post(url, fd); patch(i.id, { state: "done" }); ok++; } catch (e) { patch(i.id, { state: "error", error: formatApiError(e) }); }
    }
    setRunning(false);
    toast.success(`${ok} of ${todo.length} photos uploaded`);
    onDone();
  };

  return (
    <div className="mb-8" data-testid="bulk-upload">
      <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}
        className={`flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed p-6 text-center transition-colors duration-200 ${over ? "border-[#F08A6C] bg-[#FFF3EE]" : "border-[#23324A]/30 bg-white"}`} data-testid="bulk-dropzone">
        <FolderUp className="h-7 w-7 text-[#23324A]" />
        <p className="font-display font-extrabold text-[#23324A]">Drop a folder or photos here</p>
        <p className="text-xs text-[#23324A]/60">I'll guess the article from each file name. You pick the spot for each photo. Photos with no match go to your library.</p>
        <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { add(Array.from(e.target.files || [])); e.target.value = ""; }} data-testid="bulk-file-input" />
        <button className="chip" onClick={() => inputRef.current?.click()} data-testid="bulk-browse-btn">Or choose photos</button>
      </div>
      {items.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="bulk-list">
          {items.map((i) => <BulkRow key={i.id} item={i} rows={rows} disabled={running} onChange={(p) => patch(i.id, p)} onRemove={() => setItems((c) => c.filter((x) => x.id !== i.id))} />)}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button className="btn-coral" disabled={running || !todo.length || blocked > 0} onClick={uploadAll} data-testid="bulk-upload-all-btn">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Upload all ({todo.length})</button>
            {blocked > 0 && <p className="text-sm font-semibold text-[#D9785C]" data-testid="bulk-blocked-msg">Choose a spot for {blocked} photo{blocked === 1 ? "" : "s"} first</p>}
            <button className="chip" disabled={running} onClick={() => setItems([])} data-testid="bulk-clear-btn">Clear list</button>
          </div>
        </div>
      )}
    </div>
  );
}

function BulkRow({ item, rows, disabled, onChange, onRemove }: { item: Item; rows: ArticleOpt[]; disabled: boolean; onChange: (p: Partial<Item>) => void; onRemove: () => void }) {
  const art = rows.find((r) => r.slug === item.slug);
  const sel = "min-w-0 rounded-full border-2 border-[#23324A]/30 bg-white px-3 py-1.5 text-sm";
  const lock = disabled || item.state === "done";
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border-2 border-[#23324A]/15 bg-[#FDFBF7] p-2" data-testid="bulk-row">
      <img src={item.preview} alt={item.file.name} className="h-14 w-14 rounded-lg object-cover" />
      <p className="min-w-0 flex-1 truncate text-xs text-[#23324A]/70 sm:w-40 sm:flex-none" title={item.file.name}>{item.file.name}</p>
      <select className={`${sel} w-full sm:w-auto sm:flex-1`} value={item.slug} disabled={lock} onChange={(e) => onChange({ slug: e.target.value, spot: "" })} data-testid="bulk-article-select">
        <option value={LIB}>No article: add to my library</option>
        {rows.map((r) => <option key={r.slug} value={r.slug}>{r.title}</option>)}
      </select>
      {art && (
        <select className={`${sel} w-full sm:w-56`} value={item.spot} disabled={lock} onChange={(e) => onChange({ spot: e.target.value })} data-testid="bulk-spot-select">
          <option value="">Choose a spot…</option>
          <option value="hero">Main article photo</option>
          {art.projects.map((p) => <option key={p.index} value={String(p.index)}>Project: {p.title}</option>)}
        </select>
      )}
      <span className="text-xs font-semibold" data-testid="bulk-row-state">
        {item.state === "busy" ? <Loader2 className="h-4 w-4 animate-spin" /> : item.state === "done" ? <span className="text-[#3E8E5E]">Uploaded</span> : item.state === "error" ? <span className="text-[#D9785C]">{item.error}</span> : null}
      </span>
      {item.state !== "done" && <button className="rounded-full p-1 hover:bg-white" disabled={disabled} onClick={onRemove} aria-label="Remove" data-testid="bulk-row-remove"><X className="h-4 w-4" /></button>}
    </div>
  );
}
